import React from "react";
import { useServices } from "../../services/Services";
import ContentLoader from "../ContentLoader/ContentLoader";
import ReportContent from "../ReportContent/ReportContent";
import ReportMap from "../ReportMap/ReportMap";
import css from "./ThemedReportContent.module.css";
import { Osdk } from "@osdk/client";
import { Analysis } from "@unruly-osdk/sdk";

interface ThemedReportContentProps {
  reportId: string;
  reportText: string;
  userId?: string;
  onLinkClick?: React.MouseEventHandler<HTMLAnchorElement>;
  openLinksInNewTab?: boolean;
  onOpenAnalysis?: (analysis: Osdk.Instance<Analysis>, eventId: string) => void;
}

const ThemedReportContent: React.FC<ThemedReportContentProps> = ({
  reportId,
  reportText,
  userId,
  onLinkClick,
  openLinksInNewTab = false,
  onOpenAnalysis,
}) => {
  const { ReportTheme, Report } = useServices();
  const { data: themes, isLoading: isThemesLoading } =
    ReportTheme.useGetReportThemesByReportId(reportId);

  // Add this hook to get the country events data
  const { data: countryEvents, isLoading: isMapLoading } =
    Report.useGetReportEvents(reportId);

  if (isThemesLoading) {
    return <ContentLoader type="report-content" />;
  }

  return (
    <div className={css["themed-report-content"]}>
      {/* Report Summary */}
      <div className={css["report-summary"]}>
        <ReportContent
          reportText={reportText}
          userId={userId}
          onLinkClick={onLinkClick}
          openLinksInNewTab={openLinksInNewTab}
          onOpenAnalysis={onOpenAnalysis}
        />
      </div>

      {/* Event Distribution Map - Add this section */}
      {countryEvents && countryEvents.length > 0 && (
        <div className={css["map-section"]}>
          {isMapLoading ? (
            <ContentLoader type="map" />
          ) : (
            <ReportMap countryEvents={countryEvents} />
          )}
        </div>
      )}

      {/* Themes */}
      {themes && themes.length > 0 && (
        <div className={css["themes-section"]}>
          <h2>Key Themes</h2>
          {themes.map((themeData, index) => {
            return (
              <div key={index} className={css["theme"]}>
                <h3>{themeData.themeTitle}</h3>

                <div className={css["theme-summary"]}>
                  <ReportContent
                    reportText={themeData.themeSummary ?? ""}
                    userId={userId}
                    onLinkClick={onLinkClick}
                    openLinksInNewTab={openLinksInNewTab}
                    onOpenAnalysis={onOpenAnalysis}
                  />
                </div>

                {themeData.themeRisks && (
                  <div className={css["theme-risks"]}>
                    <h4>Risks</h4>
                    <ReportContent
                      reportText={themeData.themeRisks}
                      userId={userId}
                      onLinkClick={onLinkClick}
                      openLinksInNewTab={openLinksInNewTab}
                      onOpenAnalysis={onOpenAnalysis}
                    />
                  </div>
                )}

                {themeData.themeRecommendedActions && (
                  <div className={css["theme-actions"]}>
                    <h4>Actions</h4>
                    <ReportContent
                      reportText={themeData.themeRecommendedActions}
                      userId={userId}
                      onLinkClick={onLinkClick}
                      openLinksInNewTab={openLinksInNewTab}
                      onOpenAnalysis={onOpenAnalysis}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ThemedReportContent;
